import React, { useState, useEffect, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import JourneyListingUserDetailDto from "../../../../types/dtos/dashboards/manager/listings/journey/JourneyListingUserDetailDto";
import JourneyListingDto from "../../../../types/dtos/dashboards/manager/listings/journey/JourneyListingDto";
import { AccordionItem } from "../../../common/Accordion";
import { JourneyFormButtonArg } from "../../../../types/dashboard/JourneyFormDropDownItems";
import JourneyUserJourneyItem from "./JourneyUserJourneyItem";
import Avatar from "../../../common/Avatar";
import PaginationControls from "../../../common/PaginationControls";
import { JourneyResetPaginationContext } from "./JourneyUserView";

interface JourneyUserListItemProps {
  user: JourneyListingUserDetailDto;
  journeys: JourneyListingDto[];
  hasPreventJourneySwitchConfigEnabled: boolean;
  onUpdateClick: (journey: JourneyListingDto) => void;
  onViewClick: (journey: JourneyListingDto) => void;
  onDualPrepItemClick: (
    user: JourneyListingUserDetailDto,
    journey: JourneyListingDto,
    arg: JourneyFormButtonArg
  ) => void;
  userIndex?: number;
}

const JourneyUserListItem: React.FC<JourneyUserListItemProps> = ({
  user,
  journeys,
  hasPreventJourneySwitchConfigEnabled,
  onUpdateClick,
  onViewClick,
  onDualPrepItemClick,
  userIndex = 0,
}) => {
  const { t } = useTranslation();

  // Pagination state
  const defaultPageSize = 5;
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);

  // Get the reset counter from context
  const resetPaginationCounter = useContext(JourneyResetPaginationContext);

  // Filter journeys that belong to this user
  const userJourneys = useMemo(
    () =>
      journeys.filter(
        (journey) => journey.subjectEmployeeId === user.employeeId
      ),
    [journeys, user.employeeId]
  );

  // Get paginated journeys
  const paginatedJourneys = useMemo(() => {
    const startIndex = (pageNumber - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return userJourneys.slice(startIndex, endIndex);
  }, [userJourneys, pageNumber, pageSize]);

  // Handler for page size changes - also resets current page to 1
  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setPageNumber(1);
  };

  // Calculate status counts
  const statusCounts = {
    upcoming: userJourneys.filter((j) => j.overallStatus === "UPCOMING").length,
    notStarted: userJourneys.filter((j) => j.overallStatus === "NOT-STARTED")
      .length,
    inProgress: userJourneys.filter((j) => j.overallStatus === "IN-PROGRESS")
      .length,
    completed: userJourneys.filter((j) => j.overallStatus === "COMPLETED")
      .length,
  };

  // Reset page number when reset counter changes
  useEffect(() => {
    setPageNumber(1);
  }, [resetPaginationCounter]);

  // Content for the accordion trigger - User's name and role
  const renderUserHeader = () => {
    return (
      <div className="flex flex-col sm:flex-row sm:items-center justify-between w-full p-3 sm:p-4 bg-gray-50">
        <div className="flex items-center mb-2 sm:mb-0">
          <div className="mr-3">
            <Avatar
              imageUrl={user.profileImageUrl}
              userFullName={user.employeeFullName}
              userInitials={user.initials}
              size={10}
              activeStatus="DO-NOT-SHOW"
            />
          </div>
          <div>
            <p className="font-medium text-lg text-gray-800">
              {user.employeeFullName}
            </p>
            <p className="text-sm text-gray-600">
              {user.jobTitleTranslationKeyIdentifier
                ? t(user.jobTitleTranslationKeyIdentifier)
                : ""}
            </p>
          </div>
        </div>

        {/* Status counts */}
        <div className="flex flex-wrap gap-1 sm:gap-2">
          {statusCounts.notStarted > 0 && (
            <span className="px-2 py-1 bg-red-100 text-red-700 rounded-md text-xs">
              Not Started: {statusCounts.notStarted}
            </span>
          )}
          {statusCounts.inProgress > 0 && (
            <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded-md text-xs">
              In Progress: {statusCounts.inProgress}
            </span>
          )}
          {statusCounts.completed > 0 && (
            <span className="px-2 py-1 bg-green-100 text-green-700 rounded-md text-xs">
              Completed: {statusCounts.completed}
            </span>
          )}
          {statusCounts.upcoming > 0 && (
            <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-md text-xs">
              Upcoming: {statusCounts.upcoming}
            </span>
          )}
        </div>
      </div>
    );
  };

  // Expanded content - Journey list
  const renderJourneyList = () => (
    <div className="border-t border-gray-200 p-3 sm:p-4">
      {userJourneys.length > 0 ? (
        <>
          {/* Render journey items */}
          {paginatedJourneys.map((journey) => (
            <JourneyUserJourneyItem
              key={`journey-${journey.id}`}
              user={user}
              journey={journey}
              hasPreventJourneySwitchConfigEnabled={
                hasPreventJourneySwitchConfigEnabled
              }
              onUpdateClick={onUpdateClick}
              onViewClick={onViewClick}
              onDualPrepItemClick={onDualPrepItemClick}
            />
          ))}

          {/* Always show pagination controls when there are journeys and the number of journeys are 
              bigger than the default page size */}
          {userJourneys.length > defaultPageSize && (
            <div className="mt-4 overflow-x-auto -mx-2 sm:mx-0">
              <PaginationControls
                activePage={pageNumber}
                itemsPerPage={pageSize}
                totalItemCount={userJourneys.length}
                isLoading={false}
                loadingText={t("Common.Loading") + "..."}
                onPageChange={setPageNumber}
                onPageSizeChange={handlePageSizeChange}
              />
            </div>
          )}
        </>
      ) : (
        <p className="text-center text-gray-500 py-2">No journeys available.</p>
      )}
    </div>
  );

  const itemValue = `user-${user.employeeId}-${userIndex}`;

  return (
    <AccordionItem
      itemValue={itemValue}
      trigger={renderUserHeader()}
      content={renderJourneyList()}
      classNames="bg-white rounded-lg shadow-sm border border-gray-200 mb-4 overflow-hidden"
      triggerClassNames="w-full text-left"
      triggerBackgroundColourClassNames="bg-gray-50"
      showChevronIcons={true}
      iconClassNames="mr-4 text-gray-500"
    />
  );
};

export default JourneyUserListItem;
